import { NgModule } from '@angular/core';
import { Routes, RouterModule, ExtraOptions, RouteReuseStrategy } from '@angular/router';

import {
  MissingPetResolverService,
  MissingPetEditResolverService,
  MissingPetSubmissionResolverService,
  MissingPetDraftReportResolverService,
  PetResolverService,
} from './services/resolvers/pets-resolver.service';
import { PetplanEligiblePetsResolverService } from './services/resolvers/petplan-eligible-pets-resolver.service';
import { AuthGuard } from './services/guards/auth.guard';
import { UserMeResolverService } from './services/resolvers/user-resolver.service';
import { RoutingComponents } from './pages/pages.module';
import { InvoicesResolverService } from './services/resolvers/invoices-resolver.service';
import { CustomRouteReuseStrategy } from './app.custom-route-reuse-strategy';
import { PerksResolverService } from './services/resolvers/perks-resolver.service';
import {
  ACCOUNT_TYPE_PREVIEW_ROUTE,
  ACTIVATE_NO_EMAIL_USER_ROUTE,
  CLAIM_ACCOUNT_ROUTE,
  EMAIL_ACTIVATE_ROUTE,
  EMAIL_SENT_ROUTE,
  IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE,
  MEMBERSHIP_OPTIONS_ROUTE,
  OWNER_DETAILS_ROUTE,
  QR_CODE_PREFERENCES_ROUTE,
  QR_CODE_ROUTE,
  TOK_CLAIM_ACCOUNT,
  TOK_DETAILS,
  TOK_LOGIN,
  TOK_PAYMENT,
} from './constants/route.constants';
import {
  QrCodeDataResolverService,
  QrCodePreferencesResolverService,
} from './services/resolvers/qr-code-resolver.service';
import { DashboardInitGuard } from './services/guards/dashboard-init.guard';
import { B2CLoginInitGuard } from './services/guards/b2c-login-init.guard';
import { B2CLoginEnabledGuard } from './services/guards/b2c-login-enabled.guard';
import { FeatureFlagGuard } from './services/guards/feature-flag.guard';
import { RegistrationFlowStepsResolverService } from './services/resolvers/registration-flow-steps-resolver.service';
import { PaymentConfigResolverService } from './services/resolvers/payment-config-resolver.service';
import { registrationFlowStepGuard } from './services/guards/registration-flow-step.guard';
import { userDetailsStepGuard } from './services/guards/user-details-step.guard';
import { b21kGuard } from './services/guards/b21k.guard';
import { RegistrationFlowStep } from './constants/registration-flow.constants';
import { BRAND_ENUM } from './constants/app.constants';

export const routes: Routes = [
  {
    path: '',
    component: RoutingComponents.HomeComponent,
    canActivate: [AuthGuard],
    data: {
      sitemap: {
        priority: 1.0,
        changefreq: 'weekly',
        featureFlag: 'isHomeScreenAvailable',
      },
      meta: {
        title: 'Welcome to identi',
      },
    },
  },
  {
    path: 'cvsstaff',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    canActivate: [DashboardInitGuard],
    resolve: {
      user: UserMeResolverService,
      registrationSteps: RegistrationFlowStepsResolverService,
      paymentConfig: PaymentConfigResolverService,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.DashboardComponent,
        resolve: {
          perks: PerksResolverService,
        },
        data: {
          meta: {
            title: 'Dashboard',
          },
        },
      },
    ],
  },
  {
    path: 'identibase-plus',
    component: RoutingComponents.IdentibasePlusV2Component,
    resolve: {
      paymentConfig: PaymentConfigResolverService,
    },
    data: {
      sitemap: {
        priority: 0.8,
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'identibase+',
        description:
          'Activate your pet’s Digital ID to stay connected and contactable at all times',
      },
    },
  },
  {
    path: 'petlog-secure',
    component: RoutingComponents.IdentibasePlusPageComponent,
    data: {
      sitemap: {
        priority: 0.8,
        brands: [BRAND_ENUM.PETLOG],
      },
      meta: {
        title: 'petlogsecure',
      },
    },
  },
  {
    path: 'identialert',
    component: RoutingComponents.IdentialertV2Component,
    resolve: {
      paymentConfig: PaymentConfigResolverService,
    },
    data: {
      sitemap: {
        priority: 0.8,
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'identialert',
      },
    },
  },
  {
    path: 'petlog-reunite',
    component: RoutingComponents.IdentialertPageComponent,
    data: {
      sitemap: {
        priority: 0.8,
        brands: [BRAND_ENUM.PETLOG],
      },
      meta: {
        title: 'petlogreunite',
      },
    },
  },
  {
    path: 'acceptable-use',
    component: RoutingComponents.AcceptableUseComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Website acceptable use policy',
      },
    },
  },
  {
    path: 'account',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.AccountComponent,
        data: {
          meta: {
            title: 'My account',
          },
        },
      },
      {
        path: 'address',
        children: [
          {
            path: 'new',
            component: RoutingComponents.AddAddressComponent,
            data: {
              meta: {
                title: 'Add your address',
              },
            },
          },
          {
            path: ':id/edit',
            component: RoutingComponents.UpdateAddressComponent,
            data: {
              reloadComponent: true,
              meta: {
                title: 'Update your address',
              },
            },
          },
        ],
      },
      {
        path: 'update-email',
        component: RoutingComponents.UpdateEmailComponent,
        data: {
          meta: {
            title: 'Change account email',
          },
        },
      },
      {
        path: 'update-password',
        component: RoutingComponents.UpdatePasswordComponent,
        data: {
          meta: {
            title: 'Change password',
          },
        },
      },
      {
        path: 'update-telephone',
        component: RoutingComponents.UpdateTelephoneComponent,
        data: {
          meta: {
            title: 'Phone number',
          },
        },
      },
      {
        path: 'update-gender',
        component: RoutingComponents.UpdateGenderComponent,
        data: {
          meta: {
            title: 'Gender',
          },
        },
      },
      {
        path: 'update-title',
        component: RoutingComponents.UpdateTitleComponent,
        data: {
          meta: {
            title: 'Title',
          },
        },
      },
      {
        path: 'update-date-of-birth',
        component: RoutingComponents.UpdateDateOfBirthComponent,
        data: {
          meta: {
            title: 'Date of birth',
          },
        },
      },
      {
        path: 'account-type',
        component: RoutingComponents.UpgradeAccountWrapperComponent,
        resolve: {
          paymentConfig: PaymentConfigResolverService,
        },
        data: {
          meta: {
            title: 'Membership type',
          },
        },
      },
      {
        path: 'invoices',
        component: RoutingComponents.InvoicesComponent,
        resolve: {
          invoices: InvoicesResolverService,
        },
        data: {
          meta: {
            title: 'Invoices',
          },
        },
      },
      {
        path: 'user-details',
        component: RoutingComponents.RegisterUserDetailsConfirmationComponent,
        data: {
          isUpdateDetails: true,
          meta: {
            title: 'Your details',
          },
        },
      },
      {
        path: OWNER_DETAILS_ROUTE,
        component: RoutingComponents.OwnerDetailsComponentWrapper,
        canActivate: [userDetailsStepGuard],
        resolve: {
          registrationSteps: RegistrationFlowStepsResolverService,
        },
        data: {
          meta: {
            title: 'Owner details',
          },
        },
      },
      {
        path: MEMBERSHIP_OPTIONS_ROUTE,
        component: RoutingComponents.MembershipOptionsWrapperComponent,
        canActivate: [registrationFlowStepGuard, b21kGuard],
        resolve: {
          registrationSteps: RegistrationFlowStepsResolverService,
          paymentConfig: PaymentConfigResolverService,
        },
        data: {
          registrationFlowStep: RegistrationFlowStep.MembershipOptions,
          meta: {
            title: 'Membership options',
          },
        },
      },
      {
        path: IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE,
        component: RoutingComponents.IdentibaseSubAppreciationComponent,
        resolve: {
          registrationSteps: RegistrationFlowStepsResolverService,
        },
        data: {
          meta: {
            title: 'identi subscription route',
          },
        },
      },
      {
        path: 'subscribe-appreciation',
        component: RoutingComponents.IdentibaseSubAppreciationComponent,
        resolve: {
          registrationSteps: RegistrationFlowStepsResolverService,
        },
        data: {
          meta: {
            title: 'Thank you',
          },
        },
      },
    ],
  },
  {
    path: 'login',
    component: RoutingComponents.LoginComponent,
    canActivate: [B2CLoginEnabledGuard],
    data: {
      sitemap: {
        priority: 0.7,
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Log in',
      },
    },
  },
  {
    path: 'login-register',
    component: RoutingComponents.LoginRegisterComponent,
    canActivate: [B2CLoginEnabledGuard],
    data: {
      sitemap: {
        priority: 0.7,
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'My account',
      },
    },
  },
  {
    path: 'forgot-password',
    component: RoutingComponents.ForgotPasswordComponent,
    data: {
      sitemap: {
        priority: 0.4,
      },
      meta: {
        title: 'Forgot password',
      },
    },
  },
  {
    path: 'set-password/:token',
    component: RoutingComponents.SetNewPasswordComponent,
    data: {
      meta: {
        title: 'Set your new password',
      },
    },
  },
  {
    path: 'register',
    children: [
      {
        path: '',
        component: RoutingComponents.RegisterComponent,
        data: {
          sitemap: {
            priority: 0.9,
          },
          meta: {
            title: 'Register',
            description: 'Sign up and easily register your pet’s microchip details today',
          },
        },
      },
      {
        path: 'user-details',
        component: RoutingComponents.RegisterUserDetailsConfirmationComponent,
        resolve: {
          user: UserMeResolverService,
        },
        data: {
          meta: {
            title: 'Your details',
          },
        },
      },
    ],
  },
  {
    path: 'choose-plan',
    component: RoutingComponents.ChoosePlanWrapperComponent,
    resolve: {
      paymentConfig: PaymentConfigResolverService,
    },
    data: {
      sitemap: {
        priority: 0.8,
      },
      meta: {
        title: 'Choose a plan',
      },
    },
  },
  {
    path: 'contact-us',
    component: RoutingComponents.ContactUsWrapperComponent,
    data: {
      sitemap: {
        priority: 0.7,
      },
      meta: {
        title: 'Contact us',
        description: 'Need help with your account or lost your pet? We’re happy to help',
      },
    },
  },
  {
    path: 'about',
    component: RoutingComponents.AboutUsComponent,
    canActivate: [FeatureFlagGuard],
    data: {
      sitemap: {
        priority: 0.6,
        featureFlag: 'isPageAboutUsEnabled',
      },
      featureFlag: 'isPageAboutUsEnabled',
      meta: {
        title: 'About Us',
        description: 'The story behind identi.',
      },
    },
  },
  {
    path: 'identi-professionals',
    component: RoutingComponents.IdentiProfessionalsComponent,
    canActivate: [FeatureFlagGuard],
    data: {
      sitemap: {
        priority: 0.6,
        featureFlag: 'isPageIdentiProfessionalsEnabled',
      },
      featureFlag: 'isPageIdentiProfessionalsEnabled',
      meta: {
        title: 'identi professionals',
        description: 'Connecting breeders with unmatched support and exclusive benefits.',
      },
    },
  },
  {
    path: 'identibase-review',
    component: RoutingComponents.IdentibaseReviewComponent,
    data: {
      sitemap: {
        priority: 0.3,
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'identi 2025 year in review',
      },
    },
  },
  {
    path: 'tok-consent',
    component: RoutingComponents.TokConsentComponent,
    data: {
      meta: {
        title: 'Tok Consent',
      },
    },
  },

  {
    path: 'faq',
    redirectTo: 'contact-us',
    pathMatch: 'full',
  },
  {
    path: 'cookies',
    component: RoutingComponents.CookiesComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
      },
      meta: {
        title: 'Cookie policy',
      },
    },
  },
  {
    path: 'lost-and-found',
    component: RoutingComponents.LostAndFoundWrapperComponent,
    data: {
      sitemap: {
        priority: 0.7,
      },
      meta: {
        title: 'Lost and found',
      },
    },
  },
  {
    path: 'lost-and-found/missing-pet-report/:reference',
    component: RoutingComponents.MissingPetReportComponent,
    resolve: {
      missingPetReport: MissingPetResolverService,
    },
    data: {
      meta: {
        title: 'Lost and found missing pet report',
      },
    },
  },
  {
    path: 'lost-and-found/missing-pet/:reference',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
      skipUserMeAuth: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.MissingPetComponent,
        resolve: {
          missingPetReport: MissingPetResolverService,
        },
      },
      {
        path: 'edit',
        component: RoutingComponents.ReportMissingPetComponent,
        resolve: {
          pet: MissingPetEditResolverService,
        },
        data: {
          meta: {
            title: 'Edit your pet missing report',
          },
        },
      },
    ],
  },
  {
    path: 'rehome-pet',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.RehomePetComponent,
      },
    ],
  },
  { path: 'catsprotectioncheckyourchip', redirectTo: 'catsprotection', pathMatch: 'full' },
  {
    path: 'catsprotection',
    component: RoutingComponents.CheckPetsChipComponent,
    data: {
      sitemap: {
        priority: 0.5,
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Check your pets microchip',
      },
    },
  },
  {
    path: 'microchipping-your-pet',
    component: RoutingComponents.MicrochippingYourPetWrapperComponent,
    resolve: {
      paymentConfig: PaymentConfigResolverService,
    },
    data: {
      sitemap: {
        priority: 0.9,
      },
      meta: {
        title: 'Microchipping your pet',
        description: 'Everything you need to know about microchipping',
      },
    },
  },
  {
    path: 'my-pets',
    component: RoutingComponents.MyPetsComponent,
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      meta: {
        title: 'My pets',
      },
    },
  },
  {
    path: 'my-pet/:id',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.MyPetComponent,
        resolve: {
          pet: PetResolverService,
        },
        data: {
          meta: {
            title: 'Pet details',
          },
        },
      },
      {
        path: 'edit',
        component: RoutingComponents.EditPetWrapperComponent,
        resolve: {
          pet: PetResolverService,
          registrationSteps: RegistrationFlowStepsResolverService,
        },
        data: {
          meta: {
            title: 'Edit pet details',
          },
        },
      },
      {
        path: 'confirm',
        component: RoutingComponents.EditPetWrapperComponent,
        resolve: {
          pet: PetResolverService,
          registrationSteps: RegistrationFlowStepsResolverService,
        },
        data: {
          meta: {
            title: "Your pet's details",
          },
        },
      },
      {
        path: 'report-missing',
        component: RoutingComponents.ReportMissingPetComponent,
        resolve: {
          pet: MissingPetSubmissionResolverService,
          reports: MissingPetDraftReportResolverService,
        },
        data: {
          meta: {
            title: 'Report a pet missing',
          },
        },
      },
    ],
  },
  {
    path: 'claim-pet',
    canActivate: [b21kGuard],
    resolve: {
      user: UserMeResolverService,
      registrationSteps: RegistrationFlowStepsResolverService,
    },
    data: {
      checkDetailsProvided: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.ClaimPetByChipComponent,
        data: {
          meta: {
            title: "Your pet's details",
          },
        },
      },
    ],
  },
  {
    path: 'privacy',
    component: RoutingComponents.PrivacyComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
      },
      meta: {
        title: 'Privacy policy',
      },
    },
  },
  {
    path: 'social',
    component: RoutingComponents.SocialLandingPageComponent,
    data: {
      sitemap: {
        priority: 0.4,
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Welcome to identi',
        description:
          'identi is a national database containing the details of microchipped pets and their keepers',
      },
    },
  },
  {
    path: 'welcome-gift',
    component: RoutingComponents.PetplanWelcomePackComponent,
    canActivate: [FeatureFlagGuard, b21kGuard],
    resolve: {
      user: UserMeResolverService,
      eligiblePets: PetplanEligiblePetsResolverService,
      registrationSteps: RegistrationFlowStepsResolverService,
    },
    data: {
      featureFlag: 'isPetPlanFreeInsuranceStep4Available',
      meta: {
        title: 'Free Petplan Insurance',
      },
    },
  },
  {
    path: 'welcome-gift-lite',
    component: RoutingComponents.PetplanApplicationLiteComponent,
    resolve: {
      user: UserMeResolverService,
      eligiblePets: PetplanEligiblePetsResolverService,
    },
    data: {
      // featureFlag: 'isPetPlanFreeInsuranceStep4Available',
      meta: {
        title: 'Free Petplan Insurance',
      },
    },
  },
  {
    path: 'petplan-application',
    resolve: {
      user: UserMeResolverService,
      eligiblePets: PetplanEligiblePetsResolverService,
      registrationSteps: RegistrationFlowStepsResolverService,
    },
    data: {
      meta: {
        title: 'Free Veterinary Cover From Petplan',
      },
    },
    children: [
      {
        path: 'welcome-gift',
        component: RoutingComponents.PetplanWelcomePackComponent,
      },
    ],
  },
  {
    path: 'terms-and-conditions',
    component: RoutingComponents.TermsAndConditionsComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
      },
      meta: {
        title: 'Our terms and conditions',
      },
    },
  },
  // TODO: commented out because this route only need in development
  // {
  //   path: 'theme-kit',
  //   component: RoutingComponents.BootstrapThemeKitComponent,
  // },
  {
    path: 'website-use',
    component: RoutingComponents.WebsiteUseComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Terms of website use',
      },
    },
  },
  {
    path: ACCOUNT_TYPE_PREVIEW_ROUTE,
    component: RoutingComponents.UpgradeAccountWrapperComponent,
    resolve: {
      paymentConfig: PaymentConfigResolverService,
    },
    data: {
      skipUserMeAuth: true,
      meta: {
        title: 'Membership type',
      },
    },
  },
  {
    path: EMAIL_ACTIVATE_ROUTE,
    component: RoutingComponents.ActivateEmailComponent,
  },
  {
    path: EMAIL_SENT_ROUTE,
    component: RoutingComponents.SentEmailComponent,
  },
  {
    path: CLAIM_ACCOUNT_ROUTE,
    component: RoutingComponents.ClaimAccountComponent,
    data: {
      meta: {
        title: 'Claim account',
      },
    },
  },
  {
    path: TOK_CLAIM_ACCOUNT,
    component: RoutingComponents.TokClaimAccountComponent,
    data: {
      meta: {
        title: 'Transfer of Keepership Claim Account',
      },
    },
  },
  {
    path: TOK_LOGIN,
    component: RoutingComponents.TokLoginComponent,
    data: {
      meta: {
        title: 'Transfer of Keepership Login',
      },
    },
  },
  {
    path: ACTIVATE_NO_EMAIL_USER_ROUTE,
    component: RoutingComponents.ActivateNoEmailUserComponent,
    data: {
      meta: {
        title: 'Activate no email user',
      },
    },
  },
  {
    path: `${QR_CODE_ROUTE}/:id`,
    component: RoutingComponents.QrCodePageComponent,
    resolve: {
      qrCodeData: QrCodeDataResolverService,
    },
    data: {
      meta: {
        title: 'QR-Code Data',
      },
    },
  },
  {
    path: 'smart-tag-for-pet/:id',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
    },
    children: [
      {
        path: QR_CODE_PREFERENCES_ROUTE,
        component: RoutingComponents.QrCodePreferencesPageComponent,
        resolve: {
          qrCodePreferences: QrCodePreferencesResolverService,
          pet: PetResolverService,
        },
        data: {
          meta: {
            title: 'QR code preferences',
          },
        },
      },
    ],
  },
  {
    path: TOK_PAYMENT,
    component: RoutingComponents.TokPaymentWrapperComponent,
    canActivate: [b21kGuard],
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
      paymentConfig: PaymentConfigResolverService,
    },
    data: {
      meta: {
        title: 'tok-payment',
      },
    },
  },
  {
    path: TOK_DETAILS,
    canActivate: [b21kGuard],
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    component: RoutingComponents.TokDetailsComponent,
    data: {
      meta: {
        title: 'tok-details',
      },
    },
  },
  {
    path: 'pet-deceased',
    component: RoutingComponents.PetDeceasedComponent,
    data: {
      meta: {
        title: 'pet-deceased',
      },
    },
  },
  {
    path: 'b2c-login',
    canActivate: [B2CLoginInitGuard],
    component: RoutingComponents.B2cLoginComponent,
    data: {
      meta: {
        title: 'b2c-login',
      },
    },
  },
  {
    path: 'get-identi-smart-tag',
    component: RoutingComponents.IdentiSmartTagProductPageComponent,
    canActivate: [FeatureFlagGuard],
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      featureFlag: 'isIdentiSmartTagPlanAvailable',
      requiresProductType: 6,
      meta: {
        title: 'Get Smart tag',
      },
    },
  },
  {
    path: 'identi-smart-tag',
    component: RoutingComponents.IdentiSmartTagComponent,
    canActivate: [FeatureFlagGuard],
    data: {
      sitemap: {
        priority: 0.6,
        featureFlag: 'isPageIdentiSmartTagEnabled',
      },
      featureFlag: 'isPageIdentiSmartTagEnabled',
      meta: {
        title: 'Smart tag',
      },
    },
  },
  {
    path: '404',
    component: RoutingComponents.NotFoundComponent,
    data: {
      meta: {
        title: 'Page not found',
        prerenderStatusCode: 404,
      },
    },
  },
  {
    path: 'owner-privacy-policy',
    component: RoutingComponents.OwnerPrivacyPolicyComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Owner privacy policy',
      },
    },
  },
  {
    path: 'owner-terms-and-conditions',
    component: RoutingComponents.OwnerTermsAndConditionsComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Owner terms and conditions',
      },
    },
  },
  {
    path: 'finder-terms-of-use',
    component: RoutingComponents.FinderTermsOfUseComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Finder terms of use',
      },
    },
  },
  {
    path: 'finder-privacy-policy',
    component: RoutingComponents.FinderPrivacyPolicyComponent,
    data: {
      sitemap: {
        priority: 0.3,
        changefreq: 'yearly',
        brands: [BRAND_ENUM.DEFAULT],
      },
      meta: {
        title: 'Finder privacy policy',
      },
    },
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

const routingOptions: ExtraOptions = {
  scrollOffset: [0, 0],
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

@NgModule({
  declarations: [],
  imports: [RouterModule.forRoot(routes, routingOptions)],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
